import { Link, useLocation } from "react-router-dom";
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useTheme } from "../contexts/ThemeContext";
import { useAuth } from "../../features/auth/hooks/useAuth";
import { resetSystemData } from "../../admin/adminService";
import apiFetch from "../services/apiClient";

function useCondominioInfo() {
  const [info, setInfo] = useState({ nombre: "Cargando…", logo_url: null });
  useEffect(() => {
    apiFetch("/api/residents/condominio/")
      .then(r => r.json())
      .then(data => {
        const condo = data.results?.[0] ?? data;
        if (condo?.nombre) setInfo({ nombre: condo.nombre, logo_url: condo.logo_url ?? null });
      })
      .catch(() => {});
  }, []);
  return info;
}

// ── Nav item components ───────────────────────────────────────────────────────

function NavLink({ to, icon, children, location }) {
  const { theme } = useTheme();
  const isLight   = theme === "light";
  const active    = location.pathname === to && !location.search;

  // In light mode: always explicit color (active = dark orange, non-active = dark gray)
  // In dark mode: let CSS class handle it (undefined = no inline style)
  const itemColor = isLight ? (active ? "#c2410c" : "#374151") : undefined;

  return (
    <li>
      <Link to={to} style={{ textDecoration: "none" }}>
        <div
          className={`sb-item ${active ? "sb-item--active" : ""}`}
          style={itemColor ? { color: itemColor } : {}}
        >
          <i
            className={`fa-solid ${icon} sb-icon`}
            style={itemColor ? { color: itemColor } : {}}
          />
          <span
            className="sb-label sidebar__hide"
            style={itemColor ? { color: itemColor } : {}}
          >
            {children}
          </span>
          {active && <span className="sb-pip"/>}
        </div>
      </Link>
    </li>
  );
}

function NavGroup({ icon, label, children, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  const { theme }       = useTheme();
  const isLight         = theme === "light";

  // In light mode: open groups slightly darker, closed groups dark gray
  const btnColor = isLight ? (open ? "#111827" : "#374151") : undefined;

  function toggle() {
    const layout  = document.querySelector(".contentDashboard");
    const sidebar = document.querySelector(".contentDashboard > aside");
    const isCollapsed = layout?.classList.contains("collapsed");
    const isMobile    = window.matchMedia("(max-width: 720px)").matches;
    if (isCollapsed && !isMobile) {
      layout.classList.remove("collapsed");
      sidebar?.classList.remove("collapsed");
      return;
    }
    setOpen(v => !v);
  }

  return (
    <li>
      <button
        onClick={toggle}
        className={`sb-group-btn ${open ? "sb-group-btn--open" : ""}`}
        aria-expanded={open}
        style={btnColor ? { color: btnColor } : {}}
      >
        <div className="sb-group-left">
          <i
            className={`fa-solid ${icon} sb-icon`}
            style={btnColor ? { color: btnColor } : {}}
          />
          <span className="sb-label sidebar__hide" style={btnColor ? { color: btnColor } : {}}>
            {label}
          </span>
        </div>
        <i
          className="fa-solid fa-chevron-right sb-chevron sidebar__hide"
          style={isLight ? { color: open ? "#f97316" : "#94a3b8" } : {}}
        />
      </button>
      <div className={`sb-group-body ${open ? "sb-group-body--open" : ""}`}>
        <ul className="sb-children">
          {children}
        </ul>
      </div>
    </li>
  );
}

function NavChild({ to, icon, children, location }) {
  const { theme } = useTheme();
  const isLight   = theme === "light";
  const [path, qs] = to.split("?");
  const active =
    (location.pathname === path && !qs) ||
    (!!qs && location.pathname === path &&
      new URLSearchParams(location.search).get("tab") === new URLSearchParams("?" + qs).get("tab"));

  return (
    <li>
      <Link to={to} style={{ textDecoration: "none" }}>
        <div
          className={`sb-child ${active ? "sb-child--active" : ""}`}
          style={isLight && !active ? { color: "#4b5563" } : {}}
        >
          {icon && (
            <i
              className={`fa-solid ${icon} sb-child-icon`}
              style={isLight && !active ? { color: "#4b5563" } : {}}
            />
          )}
          <span
            className="sidebar__hide"
            style={isLight && !active ? { color: "#4b5563" } : {}}
          >
            {children}
          </span>
        </div>
      </Link>
    </li>
  );
}

// ── Main Sidebar ──────────────────────────────────────────────────────────────
export default function Sidebar({ toggleSidebar }) {
  const { logout }    = useAuth();
  const { t }         = useTranslation();
  const { theme }     = useTheme();
  const condoFromApi  = useCondominioInfo();
  const location      = useLocation();
  const isSuperUser   = localStorage.getItem("is_superuser") === "true";
  const rol           = localStorage.getItem("rol") || "USUARIO";
  const isContador    = rol === "CONTADOR";
  const isResidente   = rol === "RESIDENTE";
  const isUsuario     = !isSuperUser && !isContador && !isResidente;
  const isLight       = theme === "light";
  // For RESIDENTE, use localStorage data directly (they don't have a condominioId)
  const condo = isResidente
    ? { nombre: localStorage.getItem("condominio_nombre") || "Mi Condominio", logo_url: null }
    : condoFromApi;

  const labelColor  = isLight ? "#64748b" : undefined;  // section labels
  const mutedColor  = isLight ? "#6b7280" : undefined;  // role, footer muted

  window.closeAllSubMenus = () => {};

  async function handleReset() {
    if (!window.confirm(t("sidebar.confirmReset"))) return;
    try {
      await resetSystemData();
      window.location.reload();
    } catch {}
  }

  const isEstados = location.pathname.startsWith("/finanzas/estados") ||
    location.pathname.startsWith("/finanzas/balance-comprobacion") ||
    location.pathname.startsWith("/finanzas/estado-resultados") ||
    location.pathname.startsWith("/finanzas/balance-general") ||
    location.pathname.startsWith("/finanzas/estado-financiero");

  const TABS = [
    { labelKey: "sidebar.balanceComprobacion", tab: "balance-comprobacion", icon: "fa-scale-balanced"   },
    { labelKey: "sidebar.estadoResultados",    tab: "estado-resultados",    icon: "fa-chart-line"       },
    { labelKey: "sidebar.balanceGeneral",      tab: "balance-general",      icon: "fa-building-columns" },
    { labelKey: "sidebar.estadoFinancieroPDF", tab: "estado-financiero",    icon: "fa-file-pdf"         },
  ];

  return (
    <aside className="sb-root">
      <link rel="preconnect" href="https://fonts.googleapis.com"/>
      <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous"/>
      <link href="https://fonts.googleapis.com/css2?family=Manrope:wght@400;500;600;700;800&family=Inter:wght@300;400;500;600;700&display=swap" rel="stylesheet"/>

      {/* ── Condominio ── */}
      <div className="sb-brand">
        {condo.logo_url ? (
          <img src={condo.logo_url} alt="Logo"
            style={{ width:38, height:38, borderRadius:9, objectFit:"cover", flexShrink:0,
              background:"var(--bg-input)", border:"1px solid var(--border-card)",
              padding:2, boxSizing:"border-box" }}/>
        ) : (
          <div className="sb-brand-icon">
            <i className="fa-solid fa-building"/>
          </div>
        )}
        <div className="sb-brand-text sidebar__hide">
          <span className="sb-brand-name"
            style={isLight ? { color: "#111827" } : {}}>
            {condo.nombre}
          </span>
          <span className="sb-brand-role"
            style={mutedColor ? { color: mutedColor } : {}}>
            {isContador ? t("sidebar.contador") : isResidente ? t("portal.residente") : t("sidebar.administrador")}
          </span>
        </div>
      </div>

      {/* ── Nav ── */}
      <nav className="sb-nav hide-scrollbar">

        {isResidente ? (
          <>
            <div className="sb-section-label sidebar__hide"
              style={labelColor ? { color: labelColor } : {}}>
              {t("portal.miPortal")}
            </div>
            <ul className="sb-list">
              <NavLink to="/portal"                icon="fa-house"                 location={location}>{t("portal.inicio")}</NavLink>
              <NavLink to="/portal/reservaciones"  icon="fa-calendar-check"        location={location}>{t("portal.reservaciones")}</NavLink>
              <NavLink to="/portal/avisos-pago"    icon="fa-money-bill-transfer"   location={location}>{t("portal.avisosPago")}</NavLink>
              <NavLink to="/portal/avisos"         icon="fa-bell"                  location={location}>{t("portal.avisos")}</NavLink>
            </ul>
          </>
        ) : (
          <>
            <div className="sb-section-label sidebar__hide"
              style={labelColor ? { color: labelColor } : {}}>
              {t("sidebar.principal")}
            </div>
            <ul className="sb-list">
              <NavLink to="/" icon="fa-house" location={location}>{t("sidebar.dashboard")}</NavLink>
              <NavLink to="/residentes"   icon="fa-users"          location={location}>{t("sidebar.residentes")}</NavLink>
              <NavLink to="/proveedores" icon="fa-truck-ramp-box" location={location}>{t("sidebar.proveedores")}</NavLink>
              {isSuperUser && (
                <NavLink to="/admin" icon="fa-shield-halved" location={location}>{t("sidebar.panelAdmin")}</NavLink>
              )}
            </ul>

            <div className="sb-section-label sidebar__hide"
              style={labelColor ? { color: labelColor } : {}}>
              {t("sidebar.finanzas")}
            </div>
            <ul className="sb-list">

              {!isUsuario && (
                <NavGroup icon="fa-chart-line" label={t("sidebar.general")}>
                  <NavChild to="/finanzas/asientos"  icon="fa-book-open"        location={location}>{t("sidebar.asientosDiario")}</NavChild>
                  <NavChild to="/finanzas/catalogo"  icon="fa-sitemap"          location={location}>{t("sidebar.catalogoCuentas")}</NavChild>
                  <NavChild to="/finanzas/ingresos"  icon="fa-arrow-trend-up"   location={location}>{t("sidebar.registroIngresos")}</NavChild>
                  <NavChild to="/finanzas/gastos"    icon="fa-arrow-trend-down" location={location}>{t("sidebar.registroGastos")}</NavChild>
                  <NavChild to="/finanzas/conciliacion" icon="fa-scale-balanced" location={location}>{t("sidebar.conciliacionBancaria")}</NavChild>
                </NavGroup>
              )}

              <NavGroup icon="fa-file-invoice" label={t("sidebar.cuentasPorCobrar")}>
                <NavChild to="/finanzas/cxc"      icon="fa-gauge"            location={location}>{t("sidebar.dashboard")}</NavChild>
                <NavChild to="/finanzas/facturas" icon="fa-file-invoice"     location={location}>{t("sidebar.facturas")}</NavChild>
                <NavChild to="/finanzas/recibos"  icon="fa-receipt"          location={location}>{t("sidebar.recibos")}</NavChild>
              </NavGroup>

              <NavGroup icon="fa-file-invoice-dollar" label={t("sidebar.cuentasPorPagar")}>
                <NavChild to="/finanzas/cxp"          icon="fa-gauge"                location={location}>{t("sidebar.dashboard")}</NavChild>
                <NavChild to="/finanzas/cxp/facturas" icon="fa-file-invoice-dollar"  location={location}>{t("sidebar.facturas")}</NavChild>
                <NavChild to="/finanzas/cxp/recibos"  icon="fa-receipt"              location={location}>{t("sidebar.recibos")}</NavChild>
              </NavGroup>

              {!isUsuario && (
                <NavGroup icon="fa-chart-bar" label={t("sidebar.estadosFinancieros")}
                  defaultOpen={isEstados}>
                  {TABS.map(({ labelKey, tab, icon }) => (
                    <NavChild key={tab}
                      to={`/finanzas/estados?tab=${tab}`}
                      icon={icon} location={location}>
                      {t(labelKey)}
                    </NavChild>
                  ))}
                </NavGroup>
              )}

            </ul>

            {/* Portal Admin section */}
            <div className="sb-section-label sidebar__hide"
              style={labelColor ? { color: labelColor } : {}}>
              {t("portal.portalResidentes")}
            </div>
            <ul className="sb-list">
              <NavGroup icon="fa-mobile-screen" label={t("portal.portalResidentes")}>
                <NavChild to="/portal/admin/residentes"    icon="fa-users"                location={location}>{t("portal.residentes")}</NavChild>
                <NavChild to="/portal/admin/areas"         icon="fa-building"             location={location}>{t("portal.areasComunes")}</NavChild>
                <NavChild to="/portal/admin/reservaciones" icon="fa-calendar-check"       location={location}>{t("portal.reservaciones")}</NavChild>
                <NavChild to="/portal/admin/avisos-pago"   icon="fa-money-bill-transfer"  location={location}>{t("portal.avisosPago")}</NavChild>
                <NavChild to="/portal/admin/avisos"        icon="fa-bell"                 location={location}>{t("portal.avisosAdmin")}</NavChild>
              </NavGroup>
            </ul>
          </>
        )}

      </nav>

      {/* ── Footer ── */}
      <div className="sb-footer">
        {!isResidente && (
          <button
            className="sb-reset-btn sidebar__hide"
            onClick={handleReset}
            style={mutedColor ? { color: mutedColor } : {}}
          >
            {t("sidebar.resetearSistema")}
          </button>
        )}
        <button
          id="btnToggleSidebarCollapsed"
          className="sb-collapse-btn"
          aria-label="Toggle sidebar"
          aria-pressed="false"
          onClick={toggleSidebar}
          style={labelColor ? { color: labelColor } : {}}
        >
          <i
            className="fa-solid fa-bars"
            style={labelColor ? { color: labelColor } : {}}
          />
        </button>
      </div>
    </aside>
  );
}
